/**
 * Namespace `workspace`: os módulos do dia a dia dentro do produto — Início,
 * Equipe, Alocação, Minhas Tarefas, Calendário e Cronograma.
 *
 * Os textos foram RECORTADOS dos componentes sem reescrita. Onde dois módulos
 * repetem a mesma palavra ("Cancelar", "Salvar", "Hoje", "tarefas"), o
 * dicionário repete também: cada módulo é dono do seu texto e um dia pode
 * divergir sem arrastar os outros.
 *
 * O QUE NÃO ESTÁ AQUI, DE PROPÓSITO:
 *
 *  - `role_title` / `role_in_project` de membro e de recurso são DADO digitado
 *    pelo usuário, e o `role_title` ainda é casado com `assigned_role` dos
 *    templates de projeto pelo serviço de IA. Só os rótulos fixos do formulário
 *    ("Cargo", "Cargo no Projeto") vivem aqui; o valor nunca.
 *  - Unidades (`h`, `%`, `h/sem`) e chaves de capacidade seguem no código.
 *  - Nomes de mês e de dia da semana saíram dos arrays literais e vêm de
 *    `fmt.monthNames()` / `fmt.weekdayNames()`.
 *
 * REGRA DAS CHAVES INDEXADAS: `ReconciledStatus` (Plano × Execução), o `role`
 * de membro de organização e o `type` de item de novidade são valores de
 * negócio — comparados, gravados ou vindos do banco. Eles continuam no código e
 * aqui vive só o rótulo, indexado por esse mesmo valor.
 */
const workspace = {
  /** Módulo Início: saudação, cards de projeto, tarefas próximas e novidades. */
  home: {
    greeting: {
      morning: 'Bom dia',
      afternoon: 'Boa tarde',
      evening: 'Boa noite',
      /** Quando o perfil não tem nome nem e-mail legível. */
      userFallback: 'Usuário',
    },

    /** Carimbo de último acesso no rodapé do card de projeto. */
    lastAccess: {
      never: 'Nunca acessado',
      justNow: 'Agora mesmo',
      hoursAgo: 'há {{count}}h',
      yesterday: 'Ontem',
      daysAgo: { one: 'há {{count}} dia', other: 'há {{count}} dias' },
    },

    /** Linha abaixo da saudação, escolhida pelo estado geral da conta. */
    subtitle: {
      overdue: {
        one: 'Você tem {{count}} tarefa atrasada que precisa de atenção',
        other: 'Você tem {{count}} tarefas atrasadas que precisam de atenção',
      },
      upcoming: {
        one: 'Você tem {{count}} tarefa para os próximos 7 dias',
        other: 'Você tem {{count}} tarefas para os próximos 7 dias',
      },
      noProjects: 'Crie seu primeiro projeto para começar',
      allGood: 'Tudo em dia! Continue assim',
    },

    /** Rótulos de acessibilidade das regiões da página. */
    aria: {
      welcome: 'Boas-vindas',
      summary: 'Resumo geral',
      quickActions: 'Ações rápidas',
    },

    stats: {
      projects: 'Projetos',
      tasks: 'Tarefas',
      completed: 'Concluídas',
      overdue: 'Atrasadas',
    },

    actions: {
      newProject: 'Novo Projeto',
      continueLast: 'Continuar Último Projeto',
      search: 'Pesquisar (Ctrl+K)',
    },

    empty: {
      title: 'Nenhum projeto criado',
      description: 'Crie seu primeiro projeto para começar a gerenciar suas tarefas',
      action: 'Criar Projeto',
    },

    sections: {
      myProjects: 'Meus Projetos',
      showLess: 'Ver menos',
      showAll: 'Ver todos ({{count}})',
      /** Novidade é feminino: "Ver todos" concorda com projeto, não com ela. */
      showAllNews: 'Ver todas ({{count}})',
      upcomingTasks: 'Tarefas Próximas',
      next7Days: 'Próximos 7 dias',
      news: 'Novidades',
    },

    projectCard: {
      aria: 'Projeto {{name}}, {{progress}}% concluído',
      progressAria: '{{progress}}% concluído',
      tasks: { one: '{{count}} tarefa', other: '{{count}} tarefas' },
      overdue: { one: '{{count}} atrasada', other: '{{count}} atrasadas' },
    },

    upcoming: {
      empty: 'Nenhuma tarefa próxima do vencimento',
      /** `{{due}}` é o rótulo de vencimento já pronto ("Hoje", "Amanhã", data). */
      taskAria: 'Tarefa {{name}}, vence {{due}}',
    },

    news: {
      newBadge: 'Novo',
      /** Chave = `type` do item em `data/newsItems.ts`. */
      types: {
        feature: 'Novidade',
        improvement: 'Melhoria',
        tip: 'Dica',
      },
      date: {
        today: 'Hoje',
        yesterday: 'Ontem',
        daysAgo: { one: 'há {{count}} dia', other: 'há {{count}} dias' },
        weeksAgo: { one: 'há {{count}} semana', other: 'há {{count}} semanas' },
      },
    },

    /** Card de alerta de capacidade, acima das ações rápidas. */
    capacityAlert: {
      locked: {
        title: 'Capacidade da Equipe',
        description: 'Veja quem está sobrecarregado na sua equipe',
        cta: 'Disponível no Starter',
      },
      ok: {
        title: 'Equipe com capacidade adequada',
        description: 'Nenhum membro sobrecarregado nos próximos 28 dias',
      },
      overloaded: {
        one: '{{count}} membro sobrecarregado',
        other: '{{count}} membros sobrecarregados',
      },
      /** Colado no fim da lista de nomes quando há mais gente do que cabe. */
      andMore: ' e +{{count}}',
      projectsAtRisk: {
        one: '{{count}} projeto em risco',
        other: '{{count}} projetos em risco',
      },
      details: 'Ver detalhes',
    },
  },

  /** Módulo Equipe: cards de membro, carga de trabalho e plano de capacidade. */
  team: {
    title: 'Equipe',

    actions: {
      addMember: 'Adicionar Membro',
      importOrg: 'Importar Org',
      /** Botão tracejado no fim da grade de cards. */
      addMemberInline: 'Adicionar membro',
    },

    /** Palavra que segue o número em cada contador do cabeçalho. */
    stats: {
      member: { one: 'membro', other: 'membros' },
      assignedTask: { one: 'tarefa atribuída', other: 'tarefas atribuídas' },
      overloaded: { one: 'sobrecarregado', other: 'sobrecarregados' },
      hoursPerWeek: '{{hours}}h/sem',
      overbooking: 'overbooking',
    },

    workloadHint: 'As barras coloridas mostram a carga de cada membro. Verde = OK, Amarelo = alta, Vermelho = sobrecarregado.',

    upsell: {
      title: 'Veja a carga de trabalho da sua equipe',
      description: 'Upgrade para o Starter para ver quem está sobrecarregado',
    },

    capacity: {
      title: 'Plano de Capacidade',
      description: 'Horas planejadas/semana por recurso. A execução real vem das tarefas atribuídas (Kanban/Gantt).',
      loading: 'Carregando...',
      emptyTitle: 'Nenhum plano configurado',
      emptyDescription: 'Adicione membros à equipe acima — uma alocação inicial é criada automaticamente (editável abaixo).',
    },

    empty: {
      title: 'Sua equipe está vazia',
      description: 'Adicione membros para atribuir tarefas, acompanhar progresso e usar a matriz RACI.',
      action: '+ Adicionar Primeiro Membro',
      orgHint: 'Dica: importe membros da sua organização com um clique.',
    },

    /** Painel de novo membro. */
    add: {
      name: 'Nome *',
      namePlaceholder: 'Nome do membro',
      role: 'Cargo',
      rolePlaceholder: 'Ex: Desenvolvedor, Designer...',
      email: 'Email',
      emailPlaceholder: 'email@exemplo.com',
      avatarColor: 'Cor do avatar',
      cancel: 'Cancelar',
      submitting: 'Adicionando...',
      submit: 'Adicionar',
    },

    /** Card de membro, em leitura e em edição. */
    card: {
      namePlaceholder: 'Nome',
      rolePlaceholder: 'Cargo',
      emailPlaceholder: 'Email',
      hoursPerWeek: 'h/semana',
      cancel: 'Cancelar',
      save: 'Salvar',
      resourceBadge: 'Recurso',
      resourceBadgeTitle: 'Recurso de capacidade vinculado',
      noAccountBadge: 'Sem conta vinculada',
      noAccountHint: 'Este membro não tem conta no MeuGantt vinculada, então não recebe notificação de atribuição, prazo ou comentário. Preencha o e-mail da conta dele, ou convide-o para o projeto.',
      // Indexado pelo valor gravado em project_members.role. A chave nunca é
      // traduzida, só o rótulo: traduzir a chave quebraria o casamento.
      access: {
        owner: 'Proprietário',
        admin: 'Admin',
        editor: 'Editor',
        viewer: 'Leitor',
      },
      accessHint: 'Papel desta pessoa no projeto. É o que ela pode fazer no app, diferente do cargo, que é como você a descreve.',
      noAccessBadge: 'Sem acesso ao projeto',
      noAccessHint: 'Esta pessoa tem conta no MeuGantt mas não foi compartilhada neste projeto: ela continua atribuível e recebendo aviso, e não consegue abrir o projeto. Use Compartilhar para dar acesso.',
      inviteAction: 'Convidar',
      inviteSending: 'Enviando...',
      invitePending: 'Convite enviado',
      inviteError: 'Não foi possível convidar',
      task: { one: 'tarefa', other: 'tarefas' },
      planExec: 'Plano {{plan}}% · Execução {{exec}}%',
      edit: 'Editar',
      remove: 'Remover',
      /**
       * Chave = `ReconciledStatus`, o valor de negócio que o cálculo devolve.
       * Antes esses rótulos viviam num mapa separado, agora removido.
       */
      reconciled: {
        'no-formal-plan': 'Sem plano formal',
        'idle-plan': 'Plano ocioso',
        'exec-over-plan': 'Execução acima do plano',
        overbooking: 'Overbooking',
        critical: 'Crítico',
        attention: 'Atenção',
        available: 'Disponível',
      },
    },

    /** Barra de carga de trabalho do card. */
    workload: {
      status: {
        overloaded: 'Sobrecarregado',
        high: 'Carga alta',
        available: 'Disponível',
      },
      load: { one: 'Carga: {{count}} tarefa', other: 'Carga: {{count}} tarefas' },
      assigned: '{{hours}}h atribuídas',
      capacity: '{{hours}}h capacidade/sem',
    },

    delete: {
      title: 'Remover {{name}} da equipe?',
      noTasks: 'Este membro não possui tarefas atribuídas.',
      hasTasks: {
        one: 'Este membro possui {{count}} tarefa atribuída.',
        other: 'Este membro possui {{count}} tarefas atribuídas.',
      },
      chooseAction: 'Escolha o que fazer com as tarefas deste membro.',
      optionRemove: 'Remover atribuição (tarefas ficam sem responsável)',
      optionReassign: 'Reatribuir para:',
      selectMember: 'Selecione um membro',
      cancel: 'Cancelar',
      confirm: 'Remover',
    },

    importPanel: {
      title: 'Importar da Organização',
      description: 'Selecione membros da organização para adicionar à equipe do projeto.',
      allImported: 'Todos os membros da organização já fazem parte desta equipe.',
      selectAll: 'Selecionar todos ({{count}})',
      /** Nome exibido quando o usuário da org não tem nome nem e-mail. */
      memberFallback: 'Membro',
      cancel: 'Cancelar',
      importing: 'Importando...',
      submit: 'Importar Selecionados ({{count}})',
      /** Chave = `organization_members.role`, gravado no banco. */
      roles: {
        owner: 'Proprietário',
        admin: 'Admin',
        member: 'Membro',
      },
    },
  },

  /**
   * Tela Equipe & Alocação (handoff 14/08/2026): grade pessoa × período,
   * KPIs, painel da pessoa, nova alocação com validação e ausências.
   */
  teamAllocation: {
    title: 'Equipe & Alocação',
    subtitle: {
      one: '{{count}} pessoa da organização neste projeto',
      other: '{{count}} pessoas da organização neste projeto',
    },
    fromOrgDirectory: 'Do diretório da org',
    addPerson: 'Adicionar pessoa',
    kpi: {
      capacity: 'Horas comprometidas',
      capacityOf: '/ {{total}} de capacidade na janela',
      capacityNote: 'Capacidade já descontando feriados e ausências',
      conflicts: 'Conflitos',
      conflictsUnit: 'períodos acima da capacidade',
      conflictWorst: '{{name}} chega a {{pct}}% em {{label}}.',
      conflictNone: 'Nenhum período acima da capacidade nesta janela.',
      rebalance: 'Rebalancear',
      absences: 'Ausências',
      absencesUnit: 'registradas',
      absencesNote: 'Férias e recessos já zeram a capacidade nas células hachuradas.',
      absencesEmpty: 'Nada registrado. A capacidade exibida é teórica.',
      registerAbsence: 'Registrar ausência',
      noRaci: 'Sem papel no RACI',
      noRaciUnit: { one: 'pessoa alocada', other: 'pessoas alocadas' },
      noRaciNote: 'Quem tem horas no plano precisa de responsabilidade definida.',
      inviteN: 'Convidar ({{count}})',
      invitesSent: 'Enviados',
      openRaci: 'Abrir RACI',
    },
    toolbar: {
      search: 'Buscar pessoa, papel ou área',
      zoomDay: 'Dia',
      zoomWeek: 'Semana',
      zoomMonth: 'Mês',
      legendHealthy: 'saudável',
      legendHigh: 'alto',
      legendOver: '>100%',
      legendAbsence: 'ausência',
      legendOther: 'outro projeto',
    },
    grid: {
      person: 'Pessoa',
      raci: 'RACI',
      role: 'Papel no projeto',
      hereTotal: 'Aqui / total',
      weekSub: 'sem',
      weeksSub: '{{count}} sem',
      teamTotal: 'Total do time',
      aggregatedDemand: 'demanda agregada',
      totalsTooltip: '{{label}}: {{hours}}h de {{cap}}h ({{pct}}%)',
      tasks: { one: '{{count}} tarefa', other: '{{count}} tarefas' },
      noTasks: 'sem tarefas',
      totalPct: '{{pct}}% total',
      resourceBadge: 'RECURSO',
      inviteBadge: 'CONVIDAR',
      inviteBadgeTitle: 'Sem conta vinculada: enviar convite',
      sentBadge: 'ENVIADO',
      defineRaci: 'definir',
      defineRaciTitle: 'Sem responsabilidade definida no RACI',
      cellFree: 'Livre',
      cellFreeOther: 'Livre aqui · {{other}}h em outros projetos',
      cellTitle: '{{pct}}% ({{hours}}h aqui de {{cap}}h)',
      cellTitleOther: '{{pct}}% ({{hours}}h aqui + {{other}}h em outros projetos de {{cap}}h)',
      cellAbsent: 'Ausência registrada · capacidade zero',
      addRow: 'Adicionar pessoa da organização ou recurso genérico (ex.: "Dev Backend #2")',
    },
    panel: {
      addTitle: 'Adicionar ao projeto',
      fromOrg: 'Da organização',
      genericResource: 'Recurso genérico',
      name: 'Nome',
      namePlaceholder: 'Buscar no diretório...',
      genericPlaceholder: 'Ex.: Dev Backend #2',
      roleInProject: 'Papel no projeto',
      rolePlaceholder: 'Ex.: Desenvolvedor',
      email: 'E-mail',
      emailOptional: 'E-mail (opcional, habilita o convite)',
      color: 'Cor',
      dedication: 'Dedicação',
      orgNote: 'Capacidade total, custo/hora e ausências vêm do cadastro da organização. Aqui você define só quanto desta pessoa este projeto consome.',
      addAndAllocate: 'Adicionar e alocar',
      editRaci: 'editar na aba RACI',
      inProject: 'Neste projeto',
      otherProjects: 'Outros projetos',
      total: 'Total',
      metricsAvgNote: 'Neste projeto e Outros são médias da janela visível.',
      totalOver: 'Somando o portfólio, {{name}} está {{points}} pontos acima da capacidade de {{cap}}h/sem.',
      totalOk: 'Capacidade de {{cap}}h/sem · {{free}}h livres considerando todos os projetos.',
      access: 'Acesso à ferramenta',
      accessNone: 'Sem conta vinculada · sem acesso ao projeto',
      accessPending: 'Convite enviado · aguardando aceite',
      accessGeneric: 'Recurso genérico · não precisa de acesso',
      accessActive: 'Conta vinculada · acesso ativo',
      sendInvite: 'Enviar convite',
      resend: 'Reenviar',
      tabAllocations: 'Alocações',
      tabAbsences: 'Ausências',
      allocStatusOk: 'Ok',
      allocStatusLimit: 'No limite',
      allocStatusOver: 'Overbooking',
      newAllocation: 'Nova alocação',
      role: 'Papel',
      hoursWeek: 'Horas/sem',
      start: 'Início',
      end: 'Fim',
      presetWholeProject: 'Todo o projeto',
      presetNext4: 'Próx. 4 sem',
      presetNext12: 'Próx. 12 sem',
      impactOver: '{{name}} chegaria a {{hours}}h na semana de pico (capacidade {{cap}}h). Reduza para {{suggested}}h ou mova o início.',
      impactOk: 'Pico projetado: {{hours}}h de {{cap}}h. Sem conflito na janela escolhida.',
      allocate: 'Alocar',
      duplicate: 'Duplicar',
      absenceType: 'Tipo',
      absenceTypes: {
        ferias: 'Férias',
        recesso: 'Feriado / recesso',
        licenca: 'Licença',
        treinamento: 'Treinamento',
      },
      absenceImpactChip: 'capacidade 0h',
      absenceEmpty: 'Nenhuma ausência registrada nesta pessoa.',
      registerAbsenceTitle: 'Registrar ausência',
      absenceNote: 'Zera a capacidade de {{name}} no período e recalcula os conflitos das semanas afetadas.',
      registerAndRecalc: 'Registrar e recalcular',
      saveError: 'Não foi possível salvar. Tente de novo em instantes.',
      removeAllocation: 'Remover alocação',
      editAllocation: 'Editar alocação',
      removeAbsence: 'Remover',
      editMember: 'Editar membro',
      removeMember: 'Remover da equipe',
      capacityWeekly: 'Capacidade (h/sem)',
      save: 'Salvar',
      cancel: 'Cancelar',
    },
  },

  /** Módulo Alocação: plano de horas por recurso dentro do projeto. */
  allocation: {
    module: {
      title: 'Alocação',
      loading: 'Carregando alocações...',
      resource: { one: 'recurso', other: 'recursos' },
      hoursPerWeek: '{{hours}}h/sem',
      overbooking: 'overbooking',
      emptyTitle: 'Nenhum recurso cadastrado',
      emptyDescription: 'Cadastre recursos no Hub de Capacidade (Portfólio) antes de criar alocações.',
    },

    /** Tabela de alocações, usada aqui e dentro do módulo Equipe. */
    table: {
      columns: {
        resource: 'Recurso',
        roleInProject: 'Cargo no Projeto',
        hoursPerWeek: 'Horas/Semana',
        start: 'Início',
        end: 'Fim',
        status: 'Status',
      },
      /** Recurso apagado que ainda tem alocação apontando para ele. */
      unknownResource: 'Desconhecido',
      rolePlaceholder: 'Ex: Frontend Lead',
      newRolePlaceholder: 'Cargo no projeto',
      startPlaceholder: 'Início',
      endPlaceholder: 'Fim',
      aboveCalendar: 'Acima do calendário do projeto ({{hours}}h/sem)',
      /** Quantas semanas do período estouram a capacidade. */
      overflowWeeks: '({{count}} sem.)',
      selectResource: 'Selecionar recurso...',
      noResources: 'Nenhum recurso disponível. Cadastre recursos no Hub de Capacidade.',
      /** Sufixos colados no nome do recurso dentro do seletor. */
      planExec: ' — Plano {{plan}}% · Execução {{exec}}%',
      alreadyAllocated: ' · Já alocado',
      addAllocation: 'Adicionar Alocação',
      emptyTitle: 'Nenhuma alocação neste projeto',
      emptyDescription: 'Clique em "Adicionar Alocação" para alocar recursos',

      actions: {
        save: 'Salvar',
        cancel: 'Cancelar',
        remove: 'Remover',
        add: 'Adicionar',
      },

      tooltip: {
        overloadedTitle: 'Recurso sobrecarregado',
        suggestions: 'Sugestões:',
        suggestReduce: 'Reduzir horas/semana',
        suggestExtend: 'Estender o período',
        suggestRedistribute: 'Redistribuir para outro recurso',
        highLoad: 'Carga alta — estoura em {{over}} de {{total}} semanas',
        adequate: 'Capacidade adequada',
        willExceed: 'Adicionando {{hours}}h ultrapassará a capacidade de {{capacity}}h/sem',
        willBeHigh: 'Carga ficará alta: {{utilization}}% de {{capacity}}h/sem',
        willBeOk: 'Capacidade adequada: {{utilization}}% de {{capacity}}h/sem',
      },
    },
  },

  /** Módulo Minhas Tarefas: o que está atribuído ao usuário em todos os projetos. */
  myTasks: {
    title: 'Minhas Tarefas',
    loading: 'Carregando suas tarefas...',
    refresh: 'Atualizar',
    /** `{{tasks}}` e `{{projects}}` já chegam com número e substantivo. */
    subtitle: '{{tasks}} em {{projects}}',
    task: { one: 'tarefa', other: 'tarefas' },
    project: { one: 'projeto', other: 'projetos' },

    empty: {
      title: 'Nenhuma tarefa atribuída',
      description: 'Quando o gerente de projetos atribuir tarefas a você, elas aparecerão aqui.',
    },

    /** Chave = o filtro comparado com o status derivado da tarefa. */
    filters: {
      all: 'Todas',
      pending: 'Pendentes',
      in_progress: 'Em andamento',
      overdue: 'Atrasadas',
      completed: 'Concluídas',
    },
  },

  /** Módulo Calendário: grade mensal e painel do dia selecionado. */
  calendar: {
    title: 'Calendário',
    today: 'Hoje',
    /* Nome dos dois botões de seta. Botão só-ícone não tem rótulo na tela,
       então o balão É o nome dele, para o mouse e para o leitor de tela. */
    prevMonth: 'Mês anterior',
    nextMonth: 'Próximo mês',

    stats: {
      tasksInMonth: { one: 'tarefa no mês', other: 'tarefas no mês' },
      starting: 'iniciando',
      ending: 'terminando',
    },

    /** Rodapé da célula quando o dia tem mais tarefas do que cabem. */
    moreTasks: '+{{count}} mais',
    noTasksDay: 'Nenhuma tarefa neste dia',
    taskProgress: '{{progress}}% concluído',
  },

  /** Módulo Cronograma: linha do tempo semana a semana. */
  schedule: {
    title: 'Cronograma',
    firstVisit: 'Veja a linha do tempo semanal das entregas e marcos do projeto.',

    stats: {
      total: 'total',
      thisWeek: 'esta semana',
      overdue: 'atrasadas',
    },

    nav: {
      previous: '4 semanas anteriores',
      today: 'Hoje',
      next: '4 próximas semanas',
    },

    week: 'Semana {{number}}',
    task: { one: 'tarefa', other: 'tarefas' },
    noTasksWeek: 'Nenhuma tarefa nesta semana',
  },
  /**
   * Feed de novidades. A estrutura (id, tipo, data) fica em
   * `src/data/newsItems.ts`; aqui só o texto, indexado pelo mesmo id.
   */
  news: {
    'news-2026-03-05': {
      title: 'Organizações e compartilhamento de projetos',
      description: 'Crie organizações, convide membros por e-mail e compartilhe projetos com sua equipe de forma colaborativa.',
    },
    'news-2026-03-04': {
      title: 'Editor de texto rico no Kanban',
      description: 'A modal de tarefas do Kanban agora conta com editor rich text com negrito, itálico, listas e títulos. Salva automaticamente com indicador visual.',
    },
    'news-2026-03-03': {
      title: 'Kanban avançado com Sprints e layout flexível',
      description: 'Gerencie sprints no Kanban, alterne entre layout horizontal e vertical, e visualize a coluna Sprint diretamente no Gantt.',
    },
    'news-2026-03-02': {
      title: 'Geração de projetos com IA',
      description: 'Crie projetos completos com EAP, tarefas e cronograma a partir de uma descrição simples usando inteligência artificial.',
    },
    'news-2026-03-01': {
      title: 'Modo escuro',
      description: 'Alterne entre tema claro e escuro pela sidebar. Sua preferência é salva automaticamente.',
    },
    'news-2026-02-01': {
      title: 'Nova tela inicial redesenhada',
      description: 'Agora você tem uma visão completa dos seus projetos, tarefas próximas e novidades em uma tela inicial intuitiva e acessível.',
    },
    'news-2026-02-02': {
      title: 'Sistema de billing com Stripe',
      description: 'Gerencie sua assinatura, visualize planos disponíveis e acompanhe seu histórico de pagamentos diretamente pelo app.',
    },
    'news-2026-01-01': {
      title: 'Tela de reset de senha',
      description: 'Agora é possível redefinir sua senha de forma simples e segura diretamente pela tela de login.',
    },
    'news-2026-01-02': {
      title: 'Use Ctrl+K para pesquisar',
      description: 'Pressione Ctrl+K a qualquer momento para buscar tarefas, projetos e muito mais de forma rápida.',
    },
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type workspaceCopy = typeof workspace;

export default workspace;
